import {
  alpha,
  useTheme,
  Button,
  Box,
  Chip,
  CircularProgress,
  styled,
  Typography,
} from '@mui/material'
import {
  CalendarMonthRounded,
  CheckCircleRounded,
  EmojiEventsRounded,
  ImageRounded,
  LocalFireDepartmentRounded,
  ShareRounded,
  SportsScoreRounded,
  TrendingUpRounded,
} from '@mui/icons-material'
import { useContext, useState, useEffect, useMemo } from 'react'
import { GameStatsContext } from '../../Providers/GameStats/GameStats.jsx'
import { GameStateContext } from '../../Providers/GameStates/GameState.jsx'
import { Nums, Strings } from '../../utils.jsx'
import { customSnackbar } from '../../Hooks/CustomSnackbar.js'
import {
  generateWinImage,
  shareWinImage,
} from '../../utils/shareWinImage.js'

const ShareButton = styled(Button)`
  text-transform: none;
  font-weight: 800;
`

function MetricCard({ label, value, color, icon, compact = false }) {
  const theme = useTheme()

  return (
    <Box
      sx={{
        p: compact ? 1.5 : 1.75,
        minWidth: 0,
        minHeight: compact ? 106 : 124,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '18px',
        bgcolor: alpha(color, 0.09),
        border: `1px solid ${alpha(color, 0.18)}`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '10px',
          bgcolor: alpha(color, 0.14),
          color,
          '& svg': { fontSize: 19 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ mt: 1.25 }}>
        <Typography
          sx={{
            fontSize: compact ? '1.3rem' : '1.7rem',
            fontWeight: 900,
            lineHeight: 1,
            color: theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.55, color: theme.custom.mutedText, lineHeight: 1.25 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

function Stats({ isOpen }) {
  const theme = useTheme()
  const [statsData, setStatsData] = useState({})
  const [winImageBlob, setWinImageBlob] = useState(null)
  const [isPreparingWinImage, setIsPreparingWinImage] = useState(false)
  const [isSharingWinImage, setIsSharingWinImage] = useState(false)
  const { state: statsState } = useContext(GameStatsContext)
  const { state: gameState } = useContext(GameStateContext)
  const { showErrorSnackbar, showSuccessSnackbar } = customSnackbar()
  const winImageColors = useMemo(
    () => ({
      background: theme.palette.background.default,
      surface: theme.custom.surfaceElevated,
      tile: theme.custom.incorrect,
      link: theme.custom.tertiary,
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      gold: theme.custom.aura,
      goldGlow: alpha(theme.custom.aura, 0.13),
      text: theme.palette.text.primary,
      muted: theme.custom.mutedText,
      border: theme.custom.border,
    }),
    [theme],
  )

  const handleShareWin = () => {
    let winningMsg = ''

    winningMsg += `⛓️ Chaindle ${gameState.wordLength} - #${gameState.puzzle_number} ⛓️\n`

    // Get Score
    const score = gameState.upper.length + gameState.lower.length - 1
    const scoreDiff = score - gameState.bestScore
    winningMsg +=
      scoreDiff === 0 ? 'Top score!\n' : `${scoreDiff} away from the best\n`

    const maxBoxes = 11 // Including the yellow box
    const upperBoxes = gameState.upper.length
    const lowerBoxes = gameState.lower.length

    if (score <= maxBoxes) {
      winningMsg += '🟦'.repeat(upperBoxes) + '🟨' + '🟪'.repeat(lowerBoxes)
    } else {
      const maxEachSide = 5 // max boxes on either side of yellow box
      const ellipsis = '...'

      // When there are more boxes than allowed
      if (upperBoxes > maxEachSide && lowerBoxes > maxEachSide) {
        winningMsg +=
          '🟦'.repeat(maxEachSide) +
          ellipsis +
          '🟨' +
          ellipsis +
          '🟪'.repeat(maxEachSide)
      } else if (upperBoxes > maxEachSide) {
        const remainingUpper = maxBoxes - lowerBoxes - 1 // -1 for the yellow box
        winningMsg +=
          '🟦'.repeat(remainingUpper) +
          ellipsis +
          '🟨' +
          '🟪'.repeat(lowerBoxes)
      } else {
        const remainingLower = maxBoxes - upperBoxes - 1 // -1 for the yellow box
        winningMsg +=
          '🟦'.repeat(upperBoxes) +
          '🟨' +
          ellipsis +
          '🟪'.repeat(remainingLower)
      }
    }

    Strings.copyToClipboard(winningMsg)
    showSuccessSnackbar('Copied to clipboard!')
  }

  const handleShareStats = () => {
    const topScoreRate = statsState.gamesPlayed
      ? `${Nums.customToFixed(
          ((statsState.topScores ?? 0) / statsState.gamesPlayed) * 100,
        )}%`
      : 'N/A'
    let statsMsg = `⛓️ Chaindle ${gameState.wordLength} Lifetime Stats ⛓️\n`
    statsMsg += `Games Played: ${statsState.gamesPlayed}\n`
    statsMsg += `Top Scores: ${statsState.topScores}\n`
    statsMsg += `Top Score Rate: ${topScoreRate}\n`
    statsMsg += `Worst Chain: ${statsState.worstChain || 'N/A'}\n`
    statsMsg += `Average compared to best possible: ${Nums.customToFixed(
      statsState.avgDistanceFromBest,
    )}\n`
    statsMsg += `Average compared to most realistic possible: ${Nums.customToFixed(
      statsState.avgDistanceFromAvg,
    )}\n`
    statsMsg += `Longest Streak: ${statsState.longestStreak}`
    Strings.copyToClipboard(statsMsg)
    showSuccessSnackbar('Copied to clipboard!')
  }

  const handleShareWinImage = async () => {
    setIsSharingWinImage(true)
    try {
      const result = await shareWinImage(
        gameState,
        winImageColors,
        winImageBlob,
      )
      if (result !== 'cancelled') {
        showSuccessSnackbar(
          result === 'shared' ? 'Win image shared!' : 'Win image downloaded!',
        )
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        showErrorSnackbar('Could not generate the win image. Please try again.')
      }
    } finally {
      setIsSharingWinImage(false)
    }
  }

  useEffect(() => {
    if (!isOpen || !gameState.won) {
      setWinImageBlob(null)
      setIsPreparingWinImage(false)
      return undefined
    }

    let cancelled = false
    setIsPreparingWinImage(true)
    setWinImageBlob(null)

    generateWinImage(gameState, winImageColors)
      .then((blob) => {
        if (!cancelled) setWinImageBlob(blob)
      })
      .catch(() => {
        if (!cancelled) setWinImageBlob(null)
      })
      .finally(() => {
        if (!cancelled) setIsPreparingWinImage(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    gameState.link,
    gameState.lower,
    gameState.puzzle_number,
    gameState.upper,
    gameState.won,
    gameState.wordLength,
    isOpen,
    winImageColors,
  ])

  useEffect(() => {
    if (isOpen) {
      const stats = statsState
      const losses = stats.losses === 1 ? '1 game' : `${stats.losses} games`
      const streak = stats.streak === 1 ? '1 day' : `${stats.streak} days`
      const longestStreak =
        stats.longestStreak === 1 ? '1 day' : `${stats.longestStreak} days`
      const distanceBest = Nums.customToFixed(stats.avgDistanceFromBest)
      const gamesPlayed = stats.gamesPlayed
      const topScores = stats.topScores ?? 0
      const topScoreRate = gamesPlayed
        ? `${Nums.customToFixed((topScores / gamesPlayed) * 100)}%`
        : '—'
      const worstChain = stats.worstChain || '—'
      const distanceAvg = Nums.customToFixed(stats.avgDistanceFromAvg)
      const win = gameState.won
      const communityStats = gameState.communityStats
      const communitySolveCount = communityStats?.completionCount ?? 0
      const communityAverageLabel = !win
        ? 'Community average · finish to reveal'
        : communitySolveCount
        ? `Community average · ${communitySolveCount} ${
            communitySolveCount === 1 ? 'solve' : 'solves'
          }`
        : gameState.isCommunityStatsLoading
        ? 'Loading community average'
        : 'Community average unavailable'
      const [todaysBestScore, todaysUserScore, todaysAverageScore] = win
        ? [
            gameState.bestScore,
            gameState.upper.length + gameState.lower.length - 1,
            communityStats?.averageScore == null
              ? gameState.isCommunityStatsLoading
                ? '…'
                : '—'
              : Nums.customToFixed(communityStats.averageScore),
          ]
        : ['N/A', 'N/A', 'N/A']

      setStatsData({
        win,
        losses,
        streak,
        longestStreak,
        distanceBest,
        gamesPlayed,
        topScores,
        topScoreRate,
        worstChain,
        distanceAvg,
        todaysBestScore,
        todaysUserScore,
        todaysAverageScore,
        communityAverageLabel,
      })
    }
  }, [
    gameState.avgScore,
    gameState.bestScore,
    gameState.communityStats,
    gameState.isCommunityStatsLoading,
    gameState.lower.length,
    gameState.upper.length,
    gameState.won,
    isOpen,
    statsState,
  ])

  if (!isOpen) return null

  return (
    <Box sx={{ width: '100%', py: 2, px: { xs: 0.25, sm: 1 } }}>
      {statsData.win && (
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${alpha(
              theme.custom.tertiary,
              0.24,
            )}, ${alpha(theme.custom.aura, 0.12)})`,
            border: `1px solid ${alpha(theme.custom.tertiary, 0.38)}`,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              flex: '0 0 auto',
              display: 'grid',
              placeItems: 'center',
              borderRadius: '14px',
              bgcolor: theme.custom.surfaceElevated,
              color: theme.custom.aura,
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.1)',
            }}
          >
            <CheckCircleRounded />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
              Chain complete!
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, color: theme.custom.mutedText }}>
              You solved today&apos;s puzzle. Come back tomorrow for a new chain.
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ color: theme.custom.mutedText, letterSpacing: '.12em' }}>
            Today
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.05 }}>
            Puzzle performance
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${gameState.wordLength} letters · #${gameState.puzzle_number}`}
          sx={{ fontWeight: 800, bgcolor: theme.custom.surface }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 1,
        }}
      >
        <MetricCard label="Best possible" value={statsData.todaysBestScore} color={theme.palette.primary.main} icon={<EmojiEventsRounded />} />
        <MetricCard label="Your score" value={statsData.todaysUserScore} color={theme.custom.tertiary} icon={<SportsScoreRounded />} />
        <MetricCard label={statsData.communityAverageLabel} value={statsData.todaysAverageScore} color={theme.palette.secondary.main} icon={<TrendingUpRounded />} />
      </Box>

      <Typography variant="overline" sx={{ display: 'block', mt: 2.25, mb: 0.75, color: theme.custom.mutedText, letterSpacing: '.12em' }}>
        Lifetime
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1,
        }}
      >
        <MetricCard compact label="Games played" value={statsData.gamesPlayed} color={theme.palette.primary.main} icon={<CalendarMonthRounded />} />
        <MetricCard compact label="Top scores" value={statsData.topScores} color={theme.palette.primary.main} icon={<EmojiEventsRounded />} />
        <MetricCard compact label="Top-score rate" value={statsData.topScoreRate} color={theme.palette.primary.main} icon={<TrendingUpRounded />} />
        <MetricCard compact label="Current streak" value={statsData.streak} color={theme.custom.aura} icon={<LocalFireDepartmentRounded />} />
        <MetricCard compact label="Best streak" value={statsData.longestStreak} color={theme.custom.aura} icon={<EmojiEventsRounded />} />
        <MetricCard compact label="Unfinished games" value={statsData.losses} color={theme.custom.aura} icon={<SportsScoreRounded />} />
        <MetricCard compact label="Worst chain" value={statsData.worstChain} color={theme.palette.secondary.main} icon={<SportsScoreRounded />} />
        <MetricCard compact label="Average from best" value={statsData.distanceBest} color={theme.palette.secondary.main} icon={<TrendingUpRounded />} />
        <MetricCard compact label="Average from average" value={statsData.distanceAvg} color={theme.palette.secondary.main} icon={<TrendingUpRounded />} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
          mt: 2,
          pb: 0.5,
        }}
      >
        <ShareButton
          startIcon={
            isPreparingWinImage || isSharingWinImage ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ImageRounded />
            )
          }
          variant="contained"
          disabled={
            !statsData.win || isPreparingWinImage || isSharingWinImage
          }
          onClick={handleShareWinImage}
          sx={{
            gridColumn: '1 / -1',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          {isPreparingWinImage
            ? 'Preparing Win Image…'
            : isSharingWinImage
            ? 'Opening Image…'
            : 'Generate Image of Today’s Win'}
        </ShareButton>
        <ShareButton fullWidth startIcon={<ShareRounded />} variant="contained" color="primary" disabled={!statsData.win} onClick={handleShareWin}>
          Share Today's Win
        </ShareButton>
        <ShareButton fullWidth startIcon={<ShareRounded />} variant="contained" color="secondary" onClick={handleShareStats}>
          Share Lifetime Stats
        </ShareButton>
      </Box>
    </Box>
  );
}

export default Stats
